import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardTitle,
    CardBody,
    Button,
    Alert,
    Badge,
} from "reactstrap";

// Actions
import { FeesAction, CommonAction } from "../../../store/actions";

// Import Breadcrumb
import Breadcrumbs from "../../../components/Layout/Breadcrumb";

// Import DeleteModal
import Form from "./Form";

// Import DeleteModal
import DeleteModal from "../../../components/Common/DeleteModal";

// Import DataTable
import DataTable from "../../../components/Common/DataTable";

import { getDataTableQuery, getUserProfile } from "../../../helpers/functions_helper";
import service from "../../../services/projects.service";
import { toast } from "react-toastify";
import Avatar from "../../../components/Avatar";
import { useTranslation } from "react-i18next";



type ProjectProps = {
    company?: any;
}
const Project: FC<ProjectProps> = ({ company }) => {

    const { t } = useTranslation();

    document.title = `${t("Projects")} | ${t("process.env.REACT_APP_NAME")}`;

    const breadcrumbItems = [
        {
            url: "/dashboard",
            label: t("Dashboard"),
            active: false,
        },
        {
            url: "#",
            label: t("Projects"),
            active: true,
        },
    ];

    const [loader, setLoader] = useState(true);
    const dispatch = useDispatch();

    const useObj = getUserProfile('projects');

    const [loadingListData, setLoadingListData] = useState(false);
    const [datatableOptions, setDatatableOptions] = useState({
        draw: 1,
        page: 1,
        recordsTotal: 0,
        recordsFiltered: 0,
        sizePerPage: 20,
        searchText: "",
        sortField: "id",
        sortOrder: "desc",
    });
    const [vatList, setVatList] = useState([]);

    const columns = [
        {
            dataField: "DT_RowIndex",
            text: t("Sr. No."),
            sort: false,
            search: false,
        },
        // {
        //   dataField: "id",
        //   text: "ID",
        //   sort: true,
        //   search: true,
        // },
        {
            dataField: "company.company_name",
            text: t("Company"),
            sort: true,
            search: true,
        },
        {
            dataField: "name",
            text: t("Project name"),
            sort: true,
            search: true,
        },
        {
            dataField: "contact_person.name",
            text: t("Contact person"),
            sort: true,
            search: true,
        },
        {
            dataField: "partner.name",
            text: t("Partner"),
            sort: true,
            search: true,
        },
        {
            dataField: "account.account_name",
            text: t("Account"),
            sort: true,
            search: true,
            headerClasses: 'text-center',
            classes: 'text-center',
        },
        {
            dataField: "po",
            text: t("Active Purchase Order"),
            sort: true,
            search: true,
            headerClasses: 'text-center',
            classes: 'text-center',
            formatter: (rowContent: any, row: any): any => {
                return (
                    rowContent ? <Badge color="success" pill className="badge_custome">{t("Yes")}</Badge> : <Badge pill color="danger" className="badge_custome">{t("No")}</Badge>
                );
            },
        },
        {
            dataField: "action",
            text: t("Action"),
            sort: false,
            search: false,
            width: 200,
            hidden: !(useObj.USER_COMPANY === 1),
            formatter: (rowContent: any, row: any): any => {
                return (
                    <div className="d-flex gap-2 text-end">
                        <Link
                            className="btn btn-pill btn-outline-primary btn-sm"
                            to="#"
                            onClick={() => onClickEdit(row)}
                        >
                            <i className="fa-regular fa-pen-to-square"></i> {t("Edit")}
                        </Link>
                        <Link
                            className="btn btn-pill btn-outline-danger btn-sm"
                            to="#"
                            onClick={() => onClickDelete(row)}
                        >
                            <i className="fa-regular fa-trash-can"></i> {t("Delete")}
                        </Link>
                    </div>
                );
            },
        },
    ];

    const getList = async () => {
        const res = await service.getAll({ ...getDataTableQuery(datatableOptions, columns), company_id: company?.id });
        if (res.data.status === 'success') {
            setVatList(res.data.data);
            const updatedDatatableOptions = {
                ...datatableOptions,
                recordsTotal: res.data.recordsTotal,
                recordsFiltered: res.data.recordsFiltered,
            };
            setDatatableOptions(updatedDatatableOptions);
            setLoadingListData(false);
            setLoader(false);
        }
    }

    useEffect(() => {
        setLoadingListData(true);
    }, [company?.id])

    useEffect(() => {
        setVatList([]);
        setLoadingListData(true);
    }, [t])

    useEffect(() => {
        loadingListData && getList();
    }, [loadingListData])


    const onTableChange = (type: any, newState: any) => {
        let draw = datatableOptions.draw + 1;
        let page = 1;
        if (type == "search") {
            page = 1;
        } else {
            page = newState.page;
        }
        const updatedDatatableOptions = {
            ...datatableOptions,
            draw: draw,
            page: page,
            sizePerPage: newState.sizePerPage,
            searchText: newState.searchText,
            sortField: newState.sortField,
            sortOrder: newState.sortOrder,
        };
        setDatatableOptions(updatedDatatableOptions);
        setLoadingListData(true);
    };
    // Datatable -------------------------------------------------

    // Delete Form --------------------------------------------------------
    const [deleteRec, setDeleteRec] = useState({
        id: "",
        project_id: ""
    });
    const [recordName, setRecordName] = useState("");
    const [deleteModal, setDeleteModal] = useState(false);
    const [vatData, setVatData] = useState({});

    const [masterFormModal, setMasterFormModal] = useState(false);
    const [masterFormModalTitle, setMasterFormModalTitle] = useState(t("Create Project"));

    const onClickDelete = (row: any) => {
        const deleteData = {
            ...deleteRec,
            id: row.id,
            project_id: row.project_id,
        };
        setDeleteRec(deleteData);
        setRecordName(row.name);
        setDeleteModal(true);
    };

    const handleDelete = async () => {
        const res = await service.delete(deleteRec.id);
        if (res.status === 'success') {
            toast(res.message, { type: toast.TYPE.SUCCESS })
            setLoadingListData(true);
        }
        if (res.status === 'error') {
            toast(res.message, { type: toast.TYPE.ERROR })
            setLoadingListData(true);
        }
        setDeleteModal(false)
    };
    // Delete Form --------------------------------------------------------

    // Master Form -------------------------------------------------------- 


    const onClickModal = () => {
        setVatData({ company_id: company?.id });
        setMasterFormModalTitle(t("Create Project"));
        setMasterFormModal(true);
    };

    const onClickEdit = (row: any) => {
        setVatData(row);
        setMasterFormModalTitle(t("Update Project"));
        setMasterFormModal(true);
    }

    const handleSubmit = async (values: any, actions: any) => {
        actions.setSubmitting(true);
        try {
            if (values?.id && values?.project_id) {
                const res = await service.update(values);
                if (res.status === 'success') {
                    setMasterFormModal(false);
                    toast(res.message, { type: toast.TYPE.SUCCESS })
                    setLoadingListData(true);
                } else {
                    actions.setErrors(res.errors)
                }
            } else {
                const res = await service.create(values);
                if (res.status === 'success') {
                    setMasterFormModal(false)
                    toast(res.message, { type: toast.TYPE.SUCCESS })
                    setLoadingListData(true);
                } else {
                    actions.setErrors(res.errors)
                }
            }

        } catch (err: any) {
            toast(t('Something went wrong'), { type: toast.TYPE.ERROR })
        }
        actions.setSubmitting(false);
    };

    const clearFilter = () => {
        const updatedDatatableOptions = {
            ...datatableOptions,
            draw: 1,
            page: 1,
            sizePerPage: 20,
            searchText: "",
            sortField: "id",
            sortOrder: "desc",
        };
        setDatatableOptions(updatedDatatableOptions);

        setLoadingListData(true);
    };

    return (
        <React.Fragment>

            {deleteRec && (
                <DeleteModal
                    recordName={recordName}
                    modalTitle={t("Projects")}
                    show={deleteModal}
                    onDeleteClick={handleDelete}
                    onCloseClick={() => setDeleteModal(false)}
                    loading={false}
                />
            )}

            {masterFormModal && (
                <Form
                    modalTitle={masterFormModalTitle}
                    show={masterFormModal}
                    loading={loader}
                    vatData={vatData}
                    onSubmitClick={handleSubmit}
                    onCloseClick={() => setMasterFormModal(false)}
                    errorMsg={''}
                    errorData={''}
                    isTab={company?.id}
                />
            )}

            <div className={company?.id ? "" : "page-content"}>
                <Container fluid>
                    <Breadcrumbs
                        breadcrumbTitle={t("Projects")}
                        breadcrumbItems={company?.id ? [] : breadcrumbItems}
                        endAdornment={
                            useObj?.USER_COMPANY === 1 && <Link
                                to="#"
                                className="btn btn-primary btn-sm waves-effect waves-light btn-label"
                                onClick={() => onClickModal()}
                            >
                                <span className="align-middle material-symbols-outlined fw-light fs-4 label-icon">add</span> {t("Create Project")}
                            </Link>
                        }
                    />
                    <Row>
                        <Col xl="12">
                            <Card>
                                <CardBody>
                                    <DataTable
                                        columns={columns}
                                        products={vatList}
                                        datatableOptions={datatableOptions}
                                        filters={[]}
                                        onTableChange={onTableChange}
                                        loadingListData={loadingListData}
                                        clearFilter={clearFilter}
                                        slug="masters"
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Project;
